import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NavigationEnd, PRIMARY_OUTLET, Router, UrlSegment } from '@angular/router';
import { AuthService } from '@app/auth/services/auth.service';
import { ContactService } from '@app/contacts/services/contact.service';
import { DbWorkerService } from '@app/core/db/db-worker.service';
import { Response } from '@app/core/models/api.models';
import { AppFeature } from '@app/core/models/config.models';
import { WebsocketInit } from '@app/core/models/connection-status';
import { ApiService } from '@app/core/services/api.service';
import { AppConfigService } from '@app/core/services/app-config.service';
import { AppTranslateService } from '@app/core/services/app-translate.service';
import { SnackbarService } from '@app/core/services/snack-bar.service';
import { WebSocketService } from '@app/core/services/websocket.service';
import { NotificationKeyBackend } from '@app/preferences/models/settings.models';
import { NotificationService } from '@app/preferences/services/notification.service';
import { blobToUrl } from '@app/shared/utils/file.util';
import { destructureParams } from '@app/shared/utils/http.util';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  filter,
  firstValueFrom,
  map,
  Observable,
  of,
  pairwise,
  shareReplay,
  startWith,
  Subject,
  tap,
} from 'rxjs';

import { VoicemailDataSource, VoicemailFilterOptions } from '../data-sources/voicemail-data-source';
import {
  AllVoicemailNumber,
  isVoicemailRead,
  SharedVoicemail,
  Voicemail,
  VoicemailNumber,
  VoicemailType,
  WSVoicemail,
} from '../models/voicemail.models';

/**
 * Aggregate of unread voicemail counts, recomputed on every cache mutation.
 * `byMailbox` is keyed by `VoicemailNumber.number` and only populated when the
 * user has more than one mailbox (owned + shared).
 */
export interface VoicemailUnreadCounts {
  total: number;
  byMailbox: Record<string, number>;
}
import { VoicemailStorageService } from './voicemail-storage.service';

@Injectable({
  providedIn: 'root',
})
export class VoicemailService extends ApiService implements WebsocketInit {
  protected path = 'voicemails';

  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  private readonly errorSubject = new BehaviorSubject<boolean>(false);

  private readonly sharedVoicemailsSubject = new BehaviorSubject<SharedVoicemail[]>([]);
  private readonly sharedVoicemails$ = this.sharedVoicemailsSubject.asObservable();

  public readonly loading$ = this.loadingSubject.asObservable();
  public readonly hasError$ = this.errorSubject.asObservable();

  public readonly localVoicemailNumbersSubject = new BehaviorSubject<VoicemailNumber[] | null>(null);
  public readonly localVoicemailNumbers$ = this.localVoicemailNumbersSubject.asObservable();
  /**
   * Every time our URL changes (i.e. when we receive a `NavigationEnd` event),
   * update the selectedLocalNumber based on whatever is in the url. If the url contains
   * a number that doesn't belong to the user, set the value to undefined.
   */
  public readonly selectedVoicemailNumberSubject = new BehaviorSubject<VoicemailNumber | null>(null);
  public readonly selectedVoicemailNumber$ = this.selectedVoicemailNumberSubject.asObservable();

  /**
   * Total + per-mailbox unread counts, re-emitted whenever the cache or the
   * user's mailbox list changes. Multicast so multiple consumers (sidebar
   * badge, OS badge handler, etc.) share a single computation per tick.
   */
  public readonly unreadCounts$: Observable<VoicemailUnreadCounts>;

  public get allVoicemailNumber() {
    return AllVoicemailNumber;
  }

  public get currentSharedVoicemails(): SharedVoicemail[] {
    return this.sharedVoicemailsSubject.value;
  }

  private readonly voicemailsCursors = new Map<string, string | null>(); // <uuid, cursor>
  private readonly voicemailsLoaded = new Subject<Voicemail[]>();

  constructor(
    private webSocketService: WebSocketService,
    private snackbar: SnackbarService,
    private notificationService: NotificationService,
    private voicemailStorageService: VoicemailStorageService,
    private contactService: ContactService,
    private router: Router,
    private translations: AppTranslateService,
    private authService: AuthService,
    private dbWorker: DbWorkerService,
    private appConfigService: AppConfigService
  ) {
    super('users/{me}');

    this.voicemailStorageService.setContactLookup((contactId) => this.contactService.contactById(contactId));

    this.unreadCounts$ = combineLatest([
      this.voicemailStorageService.cacheChanged$.pipe(startWith(undefined)),
      this.localVoicemailNumbers$,
    ]).pipe(
      map(([, numbers]) => this.computeUnreadCounts(numbers)),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    // Seed in-memory cache from IDB so the UI can render instantly on app start,
    // before the first API page lands.
    this.dbWorker.seeded.then((result) => {
      this.voicemailStorageService.seedInMemory(result.voicemails);
    });

    router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map((event) => event as NavigationEnd)
      )
      .subscribe((navigationEndEvent) => {
        if (navigationEndEvent.url.includes('voicemail')) {
          this.setLocalNumberFromUrl(navigationEndEvent.url);
        }
      });

    // React to logout (jwtClaims$ becomes null) but skip the initial null emission
    // that occurs before the user has logged in.
    this.authService.jwtClaims$
      .pipe(
        pairwise(),
        filter(([prev, curr]) => prev !== null && curr === null)
      )
      .subscribe(() => {
        this.resetData();
      });
  }

  /**
   * Create a new VoicemailDataSource instance. Cache updates flow automatically via
   * the DataSource's subscription to bulkDataChanged$.
   */
  public createDataSource(filterOptions?: VoicemailFilterOptions): VoicemailDataSource {
    const dataSource = new VoicemailDataSource(this.voicemailStorageService);
    if (filterOptions) dataSource.setFilterOptions(filterOptions);
    return dataSource;
  }

  private resetData(): void {
    this.voicemailsCursors.clear();
    this.voicemailsLoaded.next([]);
    this.errorSubject.next(false);
    this.loadingSubject.next(false);
    this.voicemailStorageService.drop();
  }

  private computeUnreadCounts(numbers: VoicemailNumber[] | null): VoicemailUnreadCounts {
    const total = this.voicemailStorageService.getCount((vm) => !isVoicemailRead(vm));
    const byMailbox: Record<string, number> = {};
    if (numbers && numbers.length > 1) {
      for (const number of numbers) {
        byMailbox[number.number] = number.shared
          ? this.voicemailStorageService.getCount((vm) => !isVoicemailRead(vm) && vm.sharedInfo?.user === number.number)
          : this.voicemailStorageService.getCount((vm) => !isVoicemailRead(vm) && vm.sharedInfo === undefined);
      }
    }
    return { total, byMailbox };
  }

  init(): void | Error {
    // Initialize subscriptions and event handlers
    // Note: Data fetching is done separately via refreshData()
    combineLatest([this.contactService.currentUser$, this.sharedVoicemails$]).subscribe(
      ([currentUser, sharedVoicemail]) => {
        const sharedVoicemails: VoicemailNumber[] = sharedVoicemail.map((shared) => {
          return {
            number: shared.user,
            shared: true,
            uuid: shared.uuid,
            description: shared.name || undefined,
          };
        });
        if (currentUser && currentUser.ext && currentUser.ext.length > 0) {
          this.localVoicemailNumbersSubject.next([{ number: currentUser.ext }, ...sharedVoicemails]);
        } else {
          this.localVoicemailNumbersSubject.next(sharedVoicemails);
        }
      }
    );
  }

  public refreshData(): Observable<Voicemail[]> {
    this.errorSubject.next(false);
    this.voicemailsCursors.clear();
    // Reset loading state to ensure we don't return a non-completing Subject
    // This is critical for logout/login cycles where loading might have been interrupted
    this.loadingSubject.next(false);

    return this.getData(true);
  }

  public loadNextPage(): Observable<Voicemail[]> {
    return this.getData(false);
  }

  public getVoicemailsForContact(id: string, alsoSharedVoicemails = false): Observable<Voicemail[]> {
    const predicate = (voicemail: Voicemail) =>
      voicemail.contactId === id &&
      voicemail.type !== VoicemailType.RecentlyDeleted &&
      (alsoSharedVoicemails || voicemail.sharedInfo === undefined);

    return new Observable<Voicemail[]>((subscriber) => {
      subscriber.next(this.voicemailStorageService.getFilteredItems(predicate));
      const sub = this.voicemailStorageService.cacheChanged$.subscribe(() => {
        subscriber.next(this.voicemailStorageService.getFilteredItems(predicate));
      });
      return () => sub.unsubscribe();
    });
  }

  private getData(firstPage: boolean): Observable<Voicemail[]> {
    if (this.loadingSubject.value) {
      return this.voicemailsLoaded.asObservable();
    }

    if (this.allVoicemailsLoaded()) {
      return of([]);
    }

    this.loadingSubject.next(true);
    return new Observable<Voicemail[]>((subscriber) => {
      const processRequest = async () => {
        let response: Voicemail[];

        try {
          const myRequest = this.myVoicemailRequest();
          const sharedRequests = this.sharedVoicemailsRequests();

          const [voicemails, trash, ...shared] = await Promise.all([
            myRequest,
            firstPage ? firstValueFrom(this.get<Voicemail[]>('voicemails/trash')) : Promise.resolve([]),
            ...sharedRequests,
          ]);
          const sharedVoicemails: Voicemail[] = Array.prototype.concat(...shared);

          response = [...voicemails, ...trash, ...sharedVoicemails];

          this.voicemailsLoaded.next(response);

          // Upsert the items (in-memory-first; fire-and-forget IDB write-back).
          this.voicemailStorageService.upsertVoicemail(response);
        } catch (error) {
          console.error('Error fetching voicemails:', error);
          this.errorSubject.next(true);
          response = [];
        }

        this.loadingSubject.next(false);

        subscriber.next(response);
        subscriber.complete();
      };

      firstPage
        ? firstValueFrom(this.getSharedVoicemailNumbers()).then(() => {
            // The IDB orphan sweep inside purgeSharedVoicemailsNotIn runs independently of
            // the in-memory cache, so we don't need to wait for the seed here.
            const validUuids = new Set(this.sharedVoicemailsSubject.value.map((s) => s.uuid));
            this.voicemailStorageService.purgeSharedVoicemailsNotIn(validUuids);
            processRequest();
          })
        : processRequest();
    });
  }

  private myVoicemailRequest(): Promise<Voicemail[]> {
    let params = new HttpParams();
    const cursor = this.voicemailsCursors.get('all');

    if (cursor === null) {
      return Promise.resolve([]);
    } else {
      if (cursor) {
        params = params.append('cursor', cursor);
      }
      return firstValueFrom(
        this.get<{ data: Voicemail[]; cursor: string }>(
          `voicemails`,
          { params: destructureParams(params) },
          'v2/users/{me}'
        ).pipe(
          map((voicemail) => {
            this.voicemailsCursors.set('all', voicemail.cursor);
            return voicemail.data;
          })
        )
      );
    }
  }

  private sharedVoicemailsRequests(): Array<Promise<Voicemail[]>> {
    return this.sharedVoicemailsSubject.value.map((sharedVoicemail) => {
      let params = new HttpParams().append('shared_uuid', sharedVoicemail.uuid);
      const cursor = this.voicemailsCursors.get(sharedVoicemail.uuid);
      if (cursor === null) {
        return Promise.resolve([]);
      } else {
        if (cursor) {
          params = params.append('cursor', cursor);
        }
        return firstValueFrom(
          this.get<{ data: Voicemail[]; cursor: string }>(
            `voicemails`,
            { params: destructureParams(params) },
            'v2/users/{me}'
          ).pipe(
            map((voicemails) => {
              this.voicemailsCursors.set(sharedVoicemail.uuid, voicemails.cursor);
              return voicemails.data.map((voicemail) => {
                return {
                  ...voicemail,
                  sharedInfo: sharedVoicemail,
                  shared_uuid: sharedVoicemail.uuid,
                } as Voicemail;
              });
            })
          )
        );
      }
    });
  }

  private allVoicemailsLoaded(): boolean {
    let loaded = true;
    this.voicemailsCursors.forEach((cursor) => (loaded = loaded && cursor === null));

    return loaded && this.voicemailsCursors.size > 0;
  }

  public allVoicemailLoadedForCurrentNumber(): boolean {
    const vmNumber = this.selectedVoicemailNumberSubject.value;
    const number = vmNumber?.uuid ? vmNumber.uuid : 'all';
    return this.voicemailsCursors.get(number) === null;
  }

  /** Whether all voicemail pages have been fetched from the server (all mailboxes) */
  public get isAllDataLoaded(): boolean {
    return this.allVoicemailsLoaded();
  }

  public getCachedConversationWithId(conversationId: string): Voicemail | null {
    return this.voicemailStorageService.get(conversationId) ?? null;
  }

  public async updateRead(id: string, state: boolean): Promise<void> {
    await this.updateVoicemail(id, { type: state ? VoicemailType.VoicemailSaved : VoicemailType.Voicemail });
  }

  public async setVoicemailReadState(id: string, state: boolean): Promise<Response> {
    const uuid = await this.getUUIDPart(id);

    return firstValueFrom(this.post<Response>(`voicemail/${id}/${state ? 'save' : 'new'}${uuid}`, null));
  }

  public getVoicemailUrl(id: string): Observable<string> {
    return new Observable<string>((subscriber) => {
      const request = async () => {
        const uuid = await this.getUUIDPart(id);

        const response = await firstValueFrom(
          this.get<string>(`voicemail/${id}/download${uuid}`, {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            responseType: 'blob' as any,
          })
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .pipe(map((blob) => blobToUrl(blob as any)))
        );

        subscriber.next(response);
        subscriber.complete();
      };

      request();
    });
  }

  bindSocketEvents(): void {
    this.webSocketService.on('NewVoicemail', async (data: WSVoicemail) => {
      await this.handleNewVoicemailData(data);
    });

    this.webSocketService.on('MarkAsReadVoicemail', async (data: WSVoicemail) => {
      await this.updateRead(data.model.id, true);
    });

    this.webSocketService.on('MarkAsUnreadVoicemail', async (data: WSVoicemail) => {
      await this.updateRead(data.model.id, false);
    });

    this.webSocketService.on('DeletedVoicemail', async (data: WSVoicemail) => {
      try {
        await this.handleNewVoicemailData(data);
      } catch (error) {
        console.error('Failed to update voicemail on DeletedVoicemail message', error);
      }
    });

    this.webSocketService.on('FromTrashVoicemail', async (data: WSVoicemail) => {
      await this.handleNewVoicemailData(data);
    });

    this.webSocketService.on('NewVoicemailTranscription', async (data: WSVoicemail) => {
      try {
        await this.updateVoicemail(data.model.id, data.model);
      } catch (error) {
        console.error('Failed to update voicemail on NewVoicemailTranscription message', error);
      }
    });
  }

  private async handleNewVoicemailData(data: WSVoicemail) {
    // handle shared voicemail
    if (data.model.shared_uuid) {
      data.model.sharedInfo = this.sharedVoicemailsSubject.value.find(
        (sharedVm) => data.model.shared_uuid === sharedVm.uuid
      );
    }

    // show notification in case its new or saved
    this.voicemailStorageService.upsertVoicemail([data.model]);
    if (data.model.type === VoicemailType.Voicemail || data.model.type === VoicemailType.VoicemailSaved) {
      // Voicemail UI and its notification preference are hidden when calling is disabled.
      if (!this.appConfigService.isFeatureEnabled(AppFeature.EnableCalling)) {
        return;
      }
      this.snackbar.open(this.translations.instant('VOICEMAIL.NEW_RECEIVED'));
      this.notificationService.showNotification(
        {
          title: this.translations.instant('VOICEMAIL.NEW_RECEIVED'),
          body: data.model.sharedInfo?.name || data.model.sharedInfo?.user || '',
        },
        NotificationKeyBackend['Incoming Voicemail'],
        undefined,
        undefined,
        data.model.sharedInfo?.uuid
      );
    }
  }

  public async forwardVoicemail(id: string, ext: string): Promise<Response> {
    const uuid = await this.getUUIDPart(id);

    return firstValueFrom(this.post<Response>(`voicemail/${id}/forward${uuid}`, { ext: ext }));
  }

  public async retrieveVoicemail(id: string): Promise<Response> {
    const uuid = await this.getUUIDPart(id);

    return await firstValueFrom(this.post(`voicemail/${id}/new${uuid}`, null));
  }

  public async updateVoicemail(id: string, changes: Partial<Voicemail>): Promise<void> {
    this.voicemailStorageService.updateVoicemailItem(id, changes);
  }

  public async deleteVoicemail(id: string): Promise<object> {
    const uuid = await this.getUUIDPart(id);

    return firstValueFrom(this.delete(`voicemail/${id}${uuid}`));
  }

  public async deleteBatch(toBeDeletedIds: string[]): Promise<void> {
    try {
      await Promise.all(toBeDeletedIds.map((id) => this.deleteVoicemail(id)));
      await Promise.all(
        toBeDeletedIds.map((id) =>
          this.updateVoicemail(id, {
            type: VoicemailType.RecentlyDeleted,
          })
        )
      );
    } catch (error) {
      console.error('Error occurred during voicemails deletion or update:', error);
      throw error;
    }
  }

  public async restoreBatch(toBeRestoredIds: string[]): Promise<void> {
    try {
      await Promise.all(toBeRestoredIds.map((id) => this.retrieveVoicemail(id)));
      await Promise.all(
        toBeRestoredIds.map((id) =>
          this.updateVoicemail(id, {
            type: VoicemailType.Voicemail,
          })
        )
      );
    } catch (error) {
      console.error('Error occurred during voicemails restore or update:', error);
      throw new Error(error.message);
    }
  }

  private getSharedVoicemailNumbers(): Observable<SharedVoicemail[]> {
    return this.get<{ mailboxes: SharedVoicemail[] }>('voicemail/shared').pipe(
      catchError((error) => {
        console.error('Error while getting shared voicemails:', error);
        return of({ mailboxes: [] });
      }),
      map((response) => response.mailboxes),
      tap((response) => {
        this.sharedVoicemailsSubject.next(response);
      })
    );
  }

  public isAllLocalNumber(number: string | VoicemailNumber | null | undefined) {
    if (!number) {
      return false;
    }

    return typeof number === 'string'
      ? number === this.allVoicemailNumber.number
      : number.number === this.allVoicemailNumber.number;
  }

  /**
   * @param id - voicemail id
   * @returns '?shared_uuid=uuid' for shared voicemails, empty string otherwise
   */
  private async getUUIDPart(id: string): Promise<string> {
    let uuid = '';
    const voicemail = this.getCachedConversationWithId(id);

    if (voicemail && voicemail.sharedInfo) {
      uuid = `?shared_uuid=${voicemail.sharedInfo.uuid}`;
    }

    return uuid;
  }

  private setLocalNumberFromUrl(url: string) {
    const tree = this.router.parseUrl(url);
    const urlSegments: UrlSegment[] | undefined = tree.root.children[PRIMARY_OUTLET]?.segments;

    if (!urlSegments) {
      this.selectedVoicemailNumberSubject.next(null);
      return;
    }

    const voicemailSegmentIndex = urlSegments.findIndex((segment) => segment.path === 'voicemail');
    if (voicemailSegmentIndex !== -1 && voicemailSegmentIndex + 1 < urlSegments.length) {
      const numberInPath = urlSegments[voicemailSegmentIndex + 1];
      const localNumber = [this.allVoicemailNumber, ...(this.localVoicemailNumbersSubject.value || [])].find(
        (number) => number.number === numberInPath.path
      );

      // Update our subject if its value has changed
      const number = localNumber || this.allVoicemailNumber;
      if (JSON.stringify(number) !== JSON.stringify(this.selectedVoicemailNumberSubject.value)) {
        this.selectedVoicemailNumberSubject.next(number);
      }
    } else {
      this.selectedVoicemailNumberSubject.next(null);
    }
  }
}
